"use client";
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import DOMPurify from "dompurify";
import md from "@/app/utils/markdown";
import styles from "./DangerouslySetInnerHTML.module.scss";
import { ImagePreview } from "@douyinfe/semi-ui";
import { ImageViewer } from "antd-mobile";
import { isMobileDevice } from "@/app/utils/device";

interface Props extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "dangerouslySetInnerHTML" | "children"
> {
  msgContent: string;
}

// 缓存安全处理函数的结果
const markdownCache = new Map<string, string>();

// 检查字符串是否包含HTML标签的函数
const containsHTMLTags = (text: string): boolean => {
  return /<[a-z][\s\S]*>/i.test(text);
};

const safeMarkdown = (raw: string): string => {
  if (!raw || !raw.trim()) return "";

  if (/^\*{3,}$/.test(raw)) return raw;

  if (markdownCache.has(raw)) {
    return markdownCache.get(raw)!;
  }

  const purifyConfig = { ADD_ATTR: ["target"] };
  try {
    const html = md.render(raw.replace(/<br\/>/g, "\n "));
    // const sanitizedHtml = DOMPurify.sanitize(html, purifyConfig);
    // markdownCache.set(raw, sanitizedHtml);
    return html;
  } catch (error) {
    console.error("Markdown解析错误:", error);
    const fallback = DOMPurify.sanitize(raw, purifyConfig);
    markdownCache.set(raw, fallback);
    return fallback;
  }
};

// 独立的 Markdown 渲染组件
const MarkdownRenderer = React.memo(
  React.forwardRef<
    HTMLDivElement,
    {
      content: string;
      style: React.CSSProperties;
      className: string;
    } & React.HTMLAttributes<HTMLDivElement>
  >(({ content, style, className, ...restProps }, ref) => {
    return (
      <div
        {...restProps}
        dangerouslySetInnerHTML={{ __html: content }}
        className={className}
        style={style}
        ref={ref}
      />
    );
  }),
);

// 主组件
export default function DangerouslySetInnerHTML({
  msgContent,
  ...restProps
}: Props) {
  const [contentText, setContentText] = useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(""); // 使用状态管理预览图片URL

  // 处理Markdown内容
  useEffect(() => {
    if (msgContent && typeof msgContent === "string") {
      setContentText(safeMarkdown(msgContent));
    }
  }, [msgContent]);

  // 合并透传的style和内置的style（透传style优先级更高）
  const mergedStyle: React.CSSProperties = useMemo(() => {
    return {
      ...restProps.style,
    };
  }, [restProps.style]);

  // 使用useCallback缓存事件处理函数
  const handleImageClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;

    // 检查点击的是否是图片
    if (target.tagName === "IMG" && target.parentElement?.tagName !== "A") {
      const url = target.getAttribute("src");
      // 验证图片URL
      if (url && /^https?:\/\//i.test(url)) {
        e.preventDefault();
        e.stopPropagation();
        setPreviewSrc(url); // 直接设置状态
        setPreviewVisible(true);
      }
    }
  }, []);

  // 添加事件监听
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener(
        "click",
        handleImageClick as unknown as EventListener,
      );
    }

    return () => {
      if (container) {
        container.removeEventListener(
          "click",
          handleImageClick as unknown as EventListener,
        );
      }
    };
  }, [handleImageClick]);

  return (
    <>
      <MarkdownRenderer
        content={contentText}
        style={mergedStyle}
        className={`${styles.container} ${containsHTMLTags(msgContent || "") ? "" : styles.onlyText}`}
        {...restProps}
        ref={containerRef}
      />
      {isMobileDevice() ? (
        <ImageViewer
          image={previewSrc}
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
        />
      ) : (
        <ImagePreview
          onClose={() => setPreviewVisible(false)}
          src={previewSrc}
          visible={previewVisible}
          onVisibleChange={setPreviewVisible}
        />
      )}
    </>
  );
}
